import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/auth-context';
import { useToast } from '../../components/ui/use-toast';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, 
  Clock, 
  Database, 
  FileText, 
  RotateCw, 
  Settings2, 
  Zap, 
  Play, 
  Pause,
  Star,
  RefreshCw,
  BarChart,
  Award,
  Mic,
  Volume2
} from 'lucide-react';
import { Agent, AgentSettings, ExecutionLog } from '../../types/agent-types-new';
import StatCard from '../../components/agents/StatCard';
import EnhancedExecutionLogsTable from '../../components/agents/EnhancedExecutionLogsTable';
import { modelOptionsFor } from './modelOptions';
import { SettingsConflictNotice } from '../../components/agents/SettingsConflictNotice';
import TestingPlayground from '../../components/agents/TestingPlayground';
import VoiceAssistantTester from '../../components/agents/VoiceAssistantTester';
import { Card } from '../../components/ui/card';
import { agentService } from '../../lib/api/agent-service';
import { API_URL } from '../../config/api';

const AgentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("stats");
  const [settings, setSettings] = useState<AgentSettings | null>(null);
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Set up axios auth header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);
  
  // Determine if ID is likely a MongoDB ObjectId
  const isMongoId = id ? /^[0-9a-f]{24}$/i.test(id) : false;
  
  // Fetch agent details
  const { 
    data: agent, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useQuery({
    enabled: isAuthenticated && !!id,
    queryKey: ['agent', id],
    queryFn: async () => {
      // Always use the service to fetch the agent, it will handle all the edge cases
      const agent = await agentService.getAgentById(id!);
      if (!agent) {
        throw new Error('Agent not found');
      }
      return agent;
    }
  });
  
  // Fetch agent settings
  const {
    data: agentSettings,
    isLoading: settingsLoading,
    error: settingsError,
    refetch: refetchSettings
  } = useQuery({
    enabled: isAuthenticated && !!id && !!agent,
    queryKey: ['agent-settings', id],
    queryFn: async () => {
      if (!agent) return null;
      const settings = await agentService.getAgentSettings(agent._id);
      setSettings(settings);
      return settings;
    }
  });
  
  // Fetch agent logs
  const {
    data: agentLogs,
    isLoading: logsQueryLoading,
    error: logsError,
    refetch: refetchLogs
  } = useQuery({
    enabled: isAuthenticated && !!id && !!agent && activeTab === 'logs',
    queryKey: ['agent-logs', id],
    queryFn: async () => {
      if (!agent) return [];
      
      // Use agent_type if available, otherwise determine the standard agent type
      const agentType = agent.agent_type || agentService.determineAgentType(agent);
      console.log(`Fetching logs for agent type: ${agentType} (ID: ${agent._id})`);
      
      const logs = await agentService.getAgentLogs(agentType);
      setLogs(logs);
      return logs;
    }
  });
  
  // Update agent status mutation
  const updateAgentStatusMutation = useMutation({
    mutationFn: async ({ agentId, status }: { agentId: string, status: 'active' | 'paused' }) => {
      // PUT /api/agents/:id { status }; throws with the real status and body on failure.
      await agentService.updateAgentStatus(agentId, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', id] });
      toast({
        title: "Success",
        description: "Agent status updated",
        variant: "default",
      });
    },
    onError: (error) => {
      toast({
        title: "Status not changed",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  });
  
  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error('Error fetching agent details:', error);
    }
  }, [error]);
  
  // Update settings when they change
  useEffect(() => {
    if (agentSettings && !settings) {
      setSettings(agentSettings);
    }
  }, [agentSettings, settings]);
  
  // Update logs when they change
  useEffect(() => {
    if (agentLogs && agentLogs.length > 0) {
      setLogs(agentLogs);
    }
  }, [agentLogs]);
  
  const handleToggleStatus = () => {
    if (agent) {
      const newStatus = agent.status === 'paused' ? 'active' : 'paused';
      updateAgentStatusMutation.mutate({ agentId: agent._id, status: newStatus });
    }
  };
  
  const handleRefreshLogs = () => {
    setLogsLoading(true);
    refetchLogs().finally(() => {
      setLogsLoading(false);
      toast({
        title: "Success",
        description: "Logs refreshed successfully",
      });
    });
  };
  
  const handleSaveSettings = async (newSettings: AgentSettings) => {
    if (!agent) return;
    try {
      setSettings(newSettings);
      await agentService.saveAgentSettings(agent._id, newSettings);
      // Reload what the API now holds: the PUT wrote through to config, so the
      // conflicts report changes; the queryFn replaces local state with the result.
      // refetch() resolves with an error result rather than throwing, so a failed
      // reload is reported as its own outcome: saved, but what is shown is stale.
      const reload = await refetchSettings();
      if (reload.error) {
        toast({
          title: "Saved, but reload failed",
          description: `The settings were written, but reading them back failed: ${reload.error instanceof Error ? reload.error.message : String(reload.error)}. What is shown may be stale.`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Success",
        description: "Agent settings updated",
        variant: "default",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Settings not saved",
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    }
  };
  
  if (authLoading || isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Clock className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  if (isError || !agent) {
    return (
      <div className="container mx-auto py-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/agents/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <div className="text-center p-4 border border-red-200 rounded-md bg-red-50">
          <div className="text-red-600 font-semibold mb-2">Error loading agent details.</div>
          <p className="text-sm mb-4">The agent might not exist or there was an error fetching its data.</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button 
              onClick={() => refetch()} 
              variant="outline"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            
            <Button 
              variant="default"
              onClick={() => navigate("/agents/dashboard")}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Define maturity badge colors
  const maturityColor = {
    Rookie: 'bg-yellow-500',
    Senior: 'bg-blue-500',
    VP: 'bg-purple-500'
  }[agent.maturity];
  
  // Define progress bar colors
  const progressColor = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    teal: 'bg-teal-500'
  }[agent.color || 'blue'];
  
  // Mouse event handlers for the avatar video
  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current && agent.actionVideo) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(err => console.error("Video playback failed:", err));
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  return (
    <div className="container mx-auto py-6">
      <Button
        variant="ghost"
        onClick={() => navigate("/agents/dashboard")}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>
      
      <div className="bg-white rounded-lg overflow-hidden border shadow-sm mb-6">
        <div className="p-6 flex flex-col md:flex-row gap-6">
          {/* Agent Avatar with Video Hover */}
          <div 
            className="relative w-48 h-48 flex-shrink-0 rounded-lg overflow-hidden"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Static image - shown when not hovering */}
            <img
              src={agent.avatar}
              alt={agent.name}
              className={`w-full h-full object-cover transition-opacity duration-300 ${isHovering ? 'opacity-0 absolute inset-0' : 'opacity-100'}`}
            />
            
            {/* Video - shown when hovering */}
            {agent.actionVideo && (
              <video 
                ref={videoRef}
                src={agent.actionVideo}
                className={`w-full h-full object-cover transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                muted
                playsInline
                loop
              />
            )}
            
            <div className={`absolute top-2 right-2 ${maturityColor} text-white text-xs font-bold px-2 py-1 rounded-full z-10`}>
              {agent.maturity}
            </div>
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-2 text-xs">
              <span>Hover to see in action</span>
            </div>
          </div>
          
          <div className="flex-grow">
            <div className="flex flex-col md:flex-row justify-between items-start">
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold">{agent.name}</h1>
                  <span className={`ml-2 w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-green-500' : agent.status === 'paused' ? 'bg-amber-500' : 'bg-gray-400'}`}></span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline" className="capitalize">{agent.category}</Badge>
                  <Badge variant="secondary" className="capitalize">{agent.status}</Badge>
                  {agent.source && <Badge variant="outline" className="capitalize">{agent.source}</Badge>}
                </div>
              </div>
              <div className="flex gap-2 mt-4 md:mt-0">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleToggleStatus}
                >
                  {agent.status === 'active' ? (
                    <>
                      <Pause className="h-4 w-4 mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Activate
                    </>
                  )}
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setActiveTab("settings")}
                >
                  <Settings2 className="h-4 w-4 mr-2" />
                  Settings
                </Button>
              </div>
            </div>
            
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Mission</h3>
                <p className="text-sm text-gray-800 line-clamp-2">{agent.mission}</p>
              </div>
              
              <div>
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Development Progress</h3>
                  {typeof agent.progress === 'number' ? (
                    <>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-700">Status: <span className="font-medium">{agent.progress < 30 ? 'Early Development' : agent.progress < 70 ? 'Maturing' : 'Advanced'}</span></span>
                        <span className="font-medium">{agent.progress}%</span>
                      </div>
                      <Progress value={agent.progress} className="h-3 mb-2">
                        <div className={`h-full ${progressColor} rounded-full`} style={{ width: `${agent.progress}%` }} />
                      </Progress>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">not recorded</p>
                  )}
                </div>
                
                <div className="flex items-center text-sm">
                  <Clock className="h-4 w-4 mr-1 text-gray-500" />
                  <span className="text-gray-700">
                    Last updated: {agent.updatedAt ? new Date(agent.updatedAt).toLocaleString() : 'not recorded'}
                  </span>
                </div>
                
                <div className="flex items-center text-sm mt-1">
                  <Award className="h-4 w-4 mr-1 text-gray-500" />
                  <span className="text-gray-700">
                    Performance change: {typeof agent.performanceChange === 'number' ? (agent.performanceChange > 0 ? `+${agent.performanceChange}%` : `${agent.performanceChange}%`) : 'not recorded'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="stats" className="flex items-center">
            <Database className="h-4 w-4 mr-2" />
            Statistics
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center">
            <FileText className="h-4 w-4 mr-2" />
            Execution Logs
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center">
            <Settings2 className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="testing" className="flex items-center">
            <Zap className="h-4 w-4 mr-2" />
            Testing
          </TabsTrigger>
          {/* Only show Voice tab for voice-enabled agents */}
          {agent && agentService.hasVoiceCapabilities(agent) && (
            <TabsTrigger value="voice" className="flex items-center">
              <Mic className="h-4 w-4 mr-2" />
              Voice
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="stats" className="mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Requests"
              value="1,248"
              icon={<Database className="h-4 w-4 text-blue-500" />}
              color="bg-blue-500"
              trend="up"
              trendValue="+12%"
            />
            <StatCard
              title="Avg. Response Time"
              value="2.34s"
              icon={<Clock className="h-4 w-4 text-green-500" />}
              color="bg-green-500"
              trend="down"
              trendValue="-0.5s"
            />
            <StatCard
              title="Tokens per Request"
              value="1,847"
              icon={<FileText className="h-4 w-4 text-purple-500" />}
              color="bg-purple-500"
            />
            <StatCard
              title="Customer Satisfaction"
              value="4.8/5"
              icon={<Star className="h-4 w-4 text-amber-500" />}
              color="bg-amber-500"
              trend="up"
            />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Performance History</h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="flex items-center">
                    <span className="w-3 h-3 inline-block bg-blue-500 rounded-full mr-1"></span>
                    Accuracy
                  </span>
                  <span className="flex items-center">
                    <span className="w-3 h-3 inline-block bg-purple-500 rounded-full mr-1"></span>
                    Speed
                  </span>
                </div>
              </div>
              
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                {agent.performanceData?.length > 0 ? (
                  <div className="w-full h-full relative">
                    <div className="absolute inset-0 flex items-end justify-between px-4">
                      {agent.performanceData.map((value, index) => (
                        <div 
                          key={index} 
                          className="w-3 bg-blue-500 rounded-t-sm" 
                          style={{ height: `${value}%` }}
                        ></div>
                      ))}
                    </div>
                    <div className="absolute inset-x-0 bottom-0 h-px bg-gray-200"></div>
                  </div>
                ) : (
                  <div>Performance data not recorded</div>
                )}
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">Usage Statistics</h3>
                <Badge variant="outline">Last 7 days</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500 mb-1">Total Tokens</div>
                  <div className="text-2xl font-semibold">478,923</div>
                  <div className="text-xs text-green-600 mt-1">+7.2% from previous week</div>
                </div>
                
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-500 mb-1">Avg. Duration</div>
                  <div className="text-2xl font-semibold">1.89s</div>
                  <div className="text-xs text-green-600 mt-1">-0.3s from previous week</div>
                </div>
              </div>
              
              <h4 className="text-sm font-medium mb-2">Usage Breakdown</h4>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Database</span>
                    <span>210ms</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-400" style={{ width: '15%' }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>API Calls</span>
                    <span>980ms</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-400" style={{ width: '55%' }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Processing</span>
                    <span>430ms</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-400" style={{ width: '30%' }}></div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            <Card className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium">User Feedback</h3>
                <Badge variant="outline">12 Reviews</Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-amber-500 mb-1">4.8</div>
                  <div className="flex justify-center mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        className={`h-4 w-4 ${star <= 4.8 ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
                      />
                    ))}
                  </div>
                  <div className="text-sm text-gray-500">Average Rating</div>
                </div>
                
                <div className="md:col-span-2">
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <div key={rating} className="flex items-center gap-2">
                        <div className="text-sm w-2">{rating}</div>
                        <Star className="h-3 w-3 text-amber-500" />
                        <div className="flex-grow h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-500" 
                            style={{ width: `${rating === 5 ? '70%' : rating === 4 ? '20%' : rating === 3 ? '7%' : rating === 2 ? '3%' : '0%'}` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 w-8">
                          {rating === 5 ? '70%' : rating === 4 ? '20%' : rating === 3 ? '7%' : rating === 2 ? '3%' : '0%'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium mb-3">Recent Feedback</h4>
                <div className="space-y-3">
                  <div className="border rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2"></div>
                        <div>
                          <div className="text-sm font-medium">Alex Johnson</div>
                          <div className="text-xs text-gray-500">2 days ago</div>
                        </div>
                      </div>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className={`h-3 w-3 ${star <= 5 ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">
                      Really impressed with how quickly this agent responds to complex queries. The accuracy has improved significantly over the last month.
                    </p>
                  </div>
                  
                  <div className="border rounded-lg p-3">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2"></div>
                        <div>
                          <div className="text-sm font-medium">Sarah Miller</div>
                          <div className="text-xs text-gray-500">1 week ago</div>
                        </div>
                      </div>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            className={`h-3 w-3 ${star <= 4 ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">
                      Good analysis but sometimes misses nuances in the data. Would be great to see more detailed explanations.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="logs" className="mt-0">
          <EnhancedExecutionLogsTable 
            logs={logs}
            onRefresh={handleRefreshLogs}
            loading={logsLoading}
            error={logsError ? (logsError instanceof Error ? logsError.message : String(logsError)) : null}
            agentTypes={['page-role', 'page-semantic-intent', 'customer-intent', 'page-chatbot', 'voice-chatbot']}
          />
        </TabsContent>
        
        <TabsContent value="settings" className="mt-0">
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-medium">Agent Settings</h3>
              {settingsLoading && (
                <div className="text-xs text-gray-500">Loading settings...</div>
              )}
              {settingsError && (
                <div className="text-xs text-red-700 mt-1" role="alert">
                  Settings could not be loaded: {settingsError instanceof Error ? settingsError.message : String(settingsError)}
                </div>
              )}
            </div>
            
            {settings && (
              <>
                <div className="p-4 space-y-6">
                  <SettingsConflictNotice conflicts={settings.conflicts} invalid={settings.invalid} />
                  <div>
                    <label className="block text-sm font-medium mb-1">System Prompt</label>
                    <textarea
                      value={settings?.prompt || ''}
                      onChange={(e) => setSettings({...settings!, prompt: e.target.value})}
                      rows={8}
                      className="w-full p-2 border rounded-md font-mono text-sm"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Model</label>
                      <select 
                        value={settings?.model ?? ''}
                        onChange={(e) => setSettings({...settings!, model: e.target.value === '' ? null : e.target.value})}
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="">Not configured</option>
                        {modelOptionsFor(settings?.model).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Token Limit</label>
                      <input
                        type="number"
                        value={settings?.tokenLimit ?? ''}
                        placeholder="not configured"
                        onChange={(e) => setSettings({...settings!, tokenLimit: e.target.value === '' ? null : Number(e.target.value)})}
                        min={1000}
                        max={32000}
                        step={1000}
                        className="w-full p-2 border rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-sm font-medium">Temperature: {settings?.temperature != null ? Number(settings.temperature).toFixed(1) : 'not configured'}</label>
                    </div>
                    <input
                      type="number"
                      value={settings?.temperature ?? ''}
                      placeholder="not configured"
                      min={0}
                      max={1}
                      step={0.1}
                      onChange={(e) => setSettings({...settings!, temperature: e.target.value === '' ? null : Number(e.target.value)})}
                      className="w-full p-2 border rounded-md"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>More Deterministic</span>
                      <span>More Creative</span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-sm font-medium">Top P: {settings?.topP != null ? Number(settings.topP).toFixed(1) : 'not configured'}</label>
                    </div>
                    <input
                      type="number"
                      value={settings?.topP ?? ''}
                      placeholder="not configured"
                      min={0}
                      max={1}
                      step={0.1}
                      onChange={(e) => setSettings({...settings!, topP: e.target.value === '' ? null : Number(e.target.value)})}
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Output Length (tokens)</label>
                    <input
                      type="number"
                      value={settings?.maxLength ?? ''}
                      placeholder="not configured"
                      onChange={(e) => setSettings({...settings!, maxLength: e.target.value === '' ? null : Number(e.target.value)})}
                      min={100}
                      max={4000}
                      step={100}
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                </div>
                
                <div className="px-4 py-3 bg-gray-50 border-t flex justify-end space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      // Restore to the original settings from the API
                      if (agentSettings) {
                        setSettings(agentSettings);
                      }
                    }}
                  >
                    Reset
                  </Button>
                  <Button 
                    onClick={() => settings && handleSaveSettings(settings)}
                    // A malformed or disagreeing recorded value must be corrected at its source; the API
                    // refuses to write over it (409), so the page does not offer to.
                    disabled={updateAgentStatusMutation.isPending || (settings?.invalid?.length ?? 0) > 0 || (settings?.conflicts?.length ?? 0) > 0}
                  >
                    {updateAgentStatusMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="testing" className="mt-0">
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-medium">Testing Playground</h3>
            </div>
            
            <TestingPlayground agent={agent} />
          </div>
        </TabsContent>
        
        {/* Voice tab content */}
        <TabsContent value="voice" className="mt-0">
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-medium">Voice Assistant Testing</h3>
              <p className="text-sm text-muted-foreground">Test the voice assistant functionality with real speech input and output</p>
            </div>
            
            <VoiceAssistantTester agent={agent} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgentDetailPage;